import { SkeletonEvent } from '../../types.js';
import { stripTrailingSlash } from '../helpers/index.js';
import { isCspErrorForResource, showCspErrorInstructions } from '../helpers/cspErrors.js';
import { LoadFilesMode } from './manifestHandler.js';
import { getResponse } from '../helpers/getResponse.js';

class Mf {
    constructor({ name, initConfig, skeletonContext, mfCanary, version }) {
        this.preFetched = false;
        this.onManifestFetchFailure = async (error, manifestURL, mfName, assetsDir, timeStart) => {
            if (isCspErrorForResource(error, manifestURL)) {
                showCspErrorInstructions();
            }
            const { isDev, emitSkeletonEvent, fallbackResourceFunction, skeleton } = this.skeletonContext;
            if (isDev) {
                console.log('Failed getting from S3, trying from fallback');
            }
            emitSkeletonEvent(SkeletonEvent.ErrorFetchingManifest, { mfName, error, manifestURL });
            try {
                await fallbackResourceFunction(skeleton, { mfName, mfResource: assetsDir, timeStart });
                emitSkeletonEvent(SkeletonEvent.FallbackFetchingManifestSuccess, { mfName, error, manifestURL });
            }
            catch (e) {
                emitSkeletonEvent(SkeletonEvent.FallbackFetchingManifestFail, { mfName, error, manifestURL });
            }
        };
        this.name = name;
        this.skeletonContext = skeletonContext;
        this.mfCanary = mfCanary || { isInCanaryExperimentalGroup: false, isMfInCanary: false };
        this.version = version;
        this.initType = initConfig.type;
        if (initConfig.type === 'preloaded') {
            this.handleLoadedEntrypointScripts(initConfig.entrypoints);
        }
        else if (initConfig.type === 'withAssetsDir') {
            this.assetsDir = initConfig.assetsDir;
            this.manifestPath = initConfig.manifestPath || `${stripTrailingSlash(initConfig.assetsDir)}/asset-manifest.json`;
        }
        else if (initConfig.type === 'withAssetsData') {
            this.assetsManifest = initConfig.manifest;
            this.assetsDir = initConfig.assetsDir;
        }
        else {
            throw new Error(`Unexpected initialization type for MF "${this.name}": "${initConfig.type}"`);
        }
    }
    render(element, options = {}) {
        if (!this.mfInterface) {
            return;
        }
        const { emitSkeletonEvent } = this.skeletonContext;
        try {
            emitSkeletonEvent(SkeletonEvent.MfUserRenderStart, { mfName: this.name, element });
            this.mfInterface.render(element, options);
            emitSkeletonEvent(SkeletonEvent.MfUserRenderSuccess, { mfName: this.name });
        }
        catch (e) {
            emitSkeletonEvent(SkeletonEvent.MfUserRenderError, { mfName: this.name, error: e });
        }
    }
    async preFetch() {
        if (this.preFetched || this.initOncePromise) {
            return;
        }
        const { emitSkeletonEvent } = this.skeletonContext;
        if (!this.validateAssetsDataInput() || !this.validateAssetsDirInput()) {
            emitSkeletonEvent(SkeletonEvent.ErrorPreFetchMF, {
                mfName: this.name,
                error: new Error(`Attempt to prefetch MF: ${this.name} with a missing params. assetsDir: ${this.assetsDir}, manifestPath: ${this.manifestPath}`),
            });
            return;
        }
        // Getting the MF and the version from the assets dir url
        try {
            this.preFetched = true;
            const assetsDir = stripTrailingSlash(this.assetsDir);
            let manifest;
            if (this.initType === 'withAssetsData') {
                manifest = this.assetsManifest;
            }
            else {
                manifest = await getResponse(this.manifestPath, { mode: 'cors' });
            }
            await this.skeletonContext.manifestHandler.loadMfAssets({
                mfName: this.name,
                manifest,
                assetsDir,
                mode: LoadFilesMode.preFetchOnly,
            });
        }
        catch (error) {
            emitSkeletonEvent(SkeletonEvent.ErrorPreFetchMF, { mfName: this.name, error });
        }
    }
    async initOnce(timeStart, appendScript) {
        this.initOncePromise || (this.initOncePromise = this.initOnceImpl(timeStart, appendScript));
        return this.initOncePromise;
    }
    validateAssetsDirInput() {
        return !(this.initType === 'withAssetsDir' && (!this.assetsDir || !this.manifestPath));
    }
    validateAssetsDataInput() {
        return !(this.initType === 'withAssetsData' &&
            (!this.assetsManifest || Object.keys(this.assetsManifest).length === 0 || !this.assetsDir));
    }
    validateInputByInitType() {
        if (!this.validateAssetsDirInput()) {
            throw new Error(`Attempt to initialize MF: ${this.name} with a missing params. init type: ${this.initType} assetsDir: ${this.assetsDir}, manifestPath: ${this.manifestPath}
This probably means that an MF was preloaded and attempted to be initialized afterwards.`);
        }
        if (!this.validateAssetsDataInput()) {
            throw new Error(`Attempt to initialize MF: ${this.name}, init type: ${this.initType} with a missing assets manifest.`);
        }
    }
    async initOnceImpl(timeStart, appendScript) {
        if (this.mfInterface) {
            return this.mfInterface;
        }
        this.validateInputByInitType();
        const { emitSkeletonEvent } = this.skeletonContext;
        emitSkeletonEvent(SkeletonEvent.MFLoadStart, {
            mfName: this.name,
            mfCanary: this.mfCanary,
            version: this.version,
        });
        const assetsDir = stripTrailingSlash(this.assetsDir);
        if (this.initType === 'withAssetsDir') {
            emitSkeletonEvent(SkeletonEvent.StartFetchingManifest, {
                mfName: this.name,
                version: this.version?.toString() || '',
                mfResource: assetsDir,
            });
            try {
                const manifest = await getResponse(this.manifestPath, { mode: 'cors' });
                emitSkeletonEvent(SkeletonEvent.SuccessFetchingManifest, { mfName: this.name });
                await this.handleManifestFiles({ manifest, assetsDir, timeStart, appendScript });
            }
            catch (error) {
                await this.onManifestFetchFailure(error, this.manifestPath, this.name, assetsDir, timeStart);
            }
        }
        else if (this.initType === 'withAssetsData') {
            const manifest = {
                shared: this.assetsManifest.shared,
                entrypoints: this.assetsManifest.entrypoints,
                sameAsHost: this.assetsManifest.sameAsHost || false,
            };
            await this.handleManifestFiles({
                manifest,
                assetsDir,
                timeStart,
                appendScript,
            });
        }
        else {
            throw new Error(`Unexpected initialization type for MF "${this.name}": "${this.initType}"`);
        }
        return this.mfInterface;
    }
    async handleManifestFiles({ manifest, assetsDir, timeStart, appendScript, }) {
        if (!manifest) {
            return;
        }
        const { assetsLoadTime } = await this.skeletonContext.manifestHandler.loadMfAssets({
            mfName: this.name,
            manifest,
            assetsDir,
            mode: this.preFetched ? LoadFilesMode.skipPreFetch : LoadFilesMode.withPreFetch,
            appendScript: appendScript,
        });
        this.handleLoadedEntrypointScripts(manifest.entrypoints);
        this.skeletonContext.emitSkeletonEvent(SkeletonEvent.MFLoadDone, {
            mfName: this.name,
            totalLoadTime: Date.now() - timeStart,
            assetsLoadTime,
        });
    }
    handleLoadedEntrypointScripts(entrypoints) {
        const mfFile = entrypoints.find(entry => entry.startsWith('MF-'));
        if (!mfFile) {
            throw new Error(`No Main MF file found for MF: "${this.name}"`);
        }
        const { emitSkeletonEvent, mfApi } = this.skeletonContext;
        const [mfUuid] = mfFile.split('.');
        const mfInterface = window[mfUuid];
        try {
            this.mfInterface = mfInterface;
            const eventData = { mfUuid, mfName: this.name };
            emitSkeletonEvent(SkeletonEvent.MfUserInitStart, eventData);
            mfInterface.init({ api: mfApi });
            emitSkeletonEvent(SkeletonEvent.MfUserInitSuccess, eventData);
        }
        catch (e) {
            emitSkeletonEvent(SkeletonEvent.MfUserInitError, {
                error: e,
                mfName: this.name,
            });
        }
    }
}

export { Mf as default };
